import { Logout, Person } from "@mui/icons-material";
import { Avatar, Badge, Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, SxProps, Theme, Tooltip, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../Themes";
import { isRTL } from "../Helpers/Utils";
import { TextDescriptor } from "../Models/Common";

/**
 * Menu item configuration
 */
interface UserAvatarMenuItem {
    /** Menu item label */
    label: TextDescriptor;
    /** Menu item icon */
    icon?: React.ReactNode;
    /** Click handler */
    onClick: () => void;
    /** Show divider after this item */
    divider?: boolean;
}

/**
 * Props for UserAvatar component
 */
interface UserAvatarProps {
    /** Avatar size: 'small', 'medium', 'large' */
    size?: 'small' | 'medium' | 'large';
    /** Show tooltip with user name */
    showTooltip?: boolean;
    /** Additional MUI sx props */
    sx?: SxProps<Theme>;
    /** Menu items when user is authenticated */
    menuItemsWhenUserAuthenticated?: UserAvatarMenuItem[];
    /** Menu items when user is unauthenticated */
    menuItemsWhenUserUnauthenticated?: UserAvatarMenuItem[];
    /** Common menu items for both authenticated and unauthenticated states */
    commonMenuItems?: UserAvatarMenuItem[];
}

/**
 * Avatar size configuration
 */
interface AvatarSize {
    width: number;
    height: number;
    fontSize: string;
}

/**
 * Enhanced UserAvatar Component
 * 
 * Displays user profile picture with:
 * - Fallback to initials or default icon
 * - Online/offline status indicator (optional)
 * - Hover effects and animations
 * - Loading state
 * - Responsive sizing
 * - Theme-aware styling
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
    size = "large",
    showTooltip = true,
    sx = {},
    menuItemsWhenUserAuthenticated = [],
    menuItemsWhenUserUnauthenticated = [],
    commonMenuItems = [],
}) => {
    const theme = useTheme();
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // Memoize extracted values to prevent rerenders from cloudProvider reference changes
    const user = useMemo(() => cloudProvider?.user, [cloudProvider?.user]);
    const userProfile = useMemo(() => cloudProvider?.userProfile, [cloudProvider?.userProfile]);
    
    // Extract signOut function and memoize it with a stable reference
    const signOutRef = useRef(cloudProvider?.signOut);
    useEffect(() => {
        signOutRef.current = cloudProvider?.signOut;
    }, [cloudProvider?.signOut]);

    // Use refs to stabilize array props and prevent unnecessary rerenders
    const menuItemsAuthRef = useRef(menuItemsWhenUserAuthenticated);
    const menuItemsUnauthRef = useRef(menuItemsWhenUserUnauthenticated);
    const commonMenuItemsRef = useRef(commonMenuItems);

    // Update refs when props change
    useEffect(() => {
        menuItemsAuthRef.current = menuItemsWhenUserAuthenticated;
    }, [menuItemsWhenUserAuthenticated]);

    useEffect(() => {
        menuItemsUnauthRef.current = menuItemsWhenUserUnauthenticated;
    }, [menuItemsWhenUserUnauthenticated]);

    useEffect(() => {
        commonMenuItemsRef.current = commonMenuItems;
    }, [commonMenuItems]);

    const menuOpen = Boolean(anchorEl);
    // Handle menu open
    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    // Handle menu close
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    // Handle logout
    const handleLogout = useCallback(async () => {
        handleClose();
        try {
            if (signOutRef.current) {
                await signOutRef.current();
            }
        } catch (error) {
            console.error('Error logging out:', error);
        }
    }, [handleClose]);

    const handleLogin = useCallback(() => {
        window.location.href = '/login';
    }, []);

    const loginMenuItem = useMemo((): UserAvatarMenuItem => ({
        label: {
            localizationObject: {
                type: 'Internal',
                text: 'signIn'
            }
        },
        icon: <Person fontSize="small" />,
        onClick: handleLogin
    }), [handleLogin]);

    const logoutMenuItem = useMemo((): UserAvatarMenuItem => ({
        label: {
            localizationObject: {
                type: 'Internal',
                text: 'logout'
            }
        },
        icon: <Logout fontSize="small" />,
        onClick: handleLogout
    }), [handleLogout]);

    const proccessedWhenUserAuthenticatedMenuItems = useMemo((): UserAvatarMenuItem[] => {
        return [logoutMenuItem, ...menuItemsAuthRef.current];
    }, [logoutMenuItem]);

    const proccessedWhenUserUnauthenticatedMenuItems = useMemo((): UserAvatarMenuItem[] => {
        return [loginMenuItem, ...menuItemsUnauthRef.current];
    }, [loginMenuItem]);

    const menuItems = useMemo((): UserAvatarMenuItem[] => {
        return user
            ? [...commonMenuItemsRef.current, ...proccessedWhenUserAuthenticatedMenuItems]
            : [...commonMenuItemsRef.current, ...proccessedWhenUserUnauthenticatedMenuItems];
    }, [user, proccessedWhenUserAuthenticatedMenuItems, proccessedWhenUserUnauthenticatedMenuItems]);

    // Determine avatar size
    const avatarSize = useMemo((): AvatarSize => {
        switch (size) {
            case 'small':
                return { width: 32, height: 32, fontSize: '0.875rem' };
            case 'medium':
                return { width: 40, height: 40, fontSize: '1rem' };
            case 'large':
            default:
                return { width: 48, height: 48, fontSize: '1.25rem' };
        }
    }, [size]);

    // Get user initials
    const getUserInitials = useMemo((): string => {
        if (!userProfile) return '?';

        const displayName = userProfile.displayName || userProfile.attributes?.displayName || '';
        const email = userProfile.email || userProfile.attributes?.email || '';

        if (displayName) {
            const names = displayName.trim().split(' ');
            if (names.length >= 2) {
                return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
            }
            return displayName.substring(0, 2).toUpperCase();
        }

        if (email) {
            return email.substring(0, 2).toUpperCase();
        }

        return '?';
    }, [userProfile]);

    // Get display name for tooltip
    const displayName = useMemo((): string => {
        if (!userProfile) return localizationProvider.translateByType({ type: 'Internal', text: 'user' });
        return userProfile.displayName ||
            userProfile.attributes?.displayName ||
            userProfile.email ||
            userProfile.attributes?.email ||
            localizationProvider.translateByType({ type: 'Internal', text: 'user' });
    }, [userProfile, localizationProvider]);

    // Get photo URL
    const photoURL = useMemo((): string | null => {
        return userProfile?.photoURL ||
            userProfile?.attributes?.photoURL ||
            null;
    }, [userProfile]);

    // Loading state
    if (!userProfile) {
        return (
            <Skeleton
                variant="circular"
                width={avatarSize.width}
                height={avatarSize.height}
                sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
            />
        );
    }

    const avatarContent = (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={!user}
            sx={{
                '& .MuiBadge-badge': {
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.main,
                    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    '&::after': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        animation: 'ripple 1.2s infinite ease-in-out',
                        border: '1px solid currentColor',
                        content: '""',
                    },
                },
                '@keyframes ripple': {
                    '0%': {
                        transform: 'scale(.8)',
                        opacity: 1,
                    },
                    '100%': {
                        transform: 'scale(2.4)',
                        opacity: 0,
                    },
                },
            }}
        >
            <Avatar
                alt={displayName}
                src={photoURL || undefined}
                onClick={handleClick}
                sx={{
                    ...avatarSize,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: theme.shadows[2],
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: theme.shadows[4],
                    },
                    ...sx,
                }}
            >
                {!photoURL && (getUserInitials !== '?' ? getUserInitials : <Person />)}
            </Avatar>
        </Badge>
    );

    const avatarElement = (
        <Box sx={{ p: 0.5 }}>
            {avatarContent}
        </Box>
    );

    return (
        <>
            {showTooltip ? (
                <Tooltip
                    title={displayName}
                    arrow
                    placement="bottom"
                >
                    {avatarElement}
                </Tooltip>
            ) : (
                avatarElement
            )}

            {/* User Menu */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        mt: 1.5,
                        minWidth: 200,
                        overflow: 'visible',
                        filter: theme.palette.mode === 'dark'
                            ? 'drop-shadow(0px 2px 8px rgba(0,0,0,0.5))'
                            : 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                        bgcolor: 'background.paper',
                        backgroundImage: theme.palette.mode === 'dark'
                            ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))'
                            : 'none',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            [isRTL() ? 'left' : 'right']: isRTL() ? 42 : 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                {/* Custom menu items */}
                {menuItems.map((item, index) => (
                    <MenuItem
                        key={`menu-item-${index}`}
                        onClick={item.onClick}
                        sx={{
                            py: 1.5,
                            px: 2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(0, 0, 0, 0.04)',
                            }
                        }}
                    >
                        {item.icon && (
                            <ListItemIcon 
                                key={`menu-icon-${index}`}
                                sx={{
                                    color: theme.palette.mode === 'dark'
                                        ? theme.palette.primary.light
                                        : theme.palette.primary.main
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                        )}
                        <ListItemText
                            key={`menu-text-${index}`}
                            sx={{
                                color: theme.palette.text.primary,
                                '& .MuiListItemText-primary': {
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }
                            }}
                        >
                            {localizationProvider.translateByType(item.label.localizationObject)}
                        </ListItemText>
                        {item.divider && (
                            <Divider 
                                key={`menu-divider-${index}`}
                                sx={{
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.12)'
                                        : 'rgba(0, 0, 0, 0.12)'
                                }} 
                            />
                        )}
                    </MenuItem>

                ))}
            </Menu>
        </>
    );
};

export default UserAvatar;
